import { FilterInput, GridInput, IGridRequestDto } from '../../data-grid/src';

export type KeyHints = 'search' | 'limit' | 'sort' | string | any;

export function singlePageFilter(filter: GridInput = {}) {
    return {
        ...filter,
        paging: {
            page: 1,
            // Pls do not change, setting this to high number fucks up the application.
            // Let the user supply more specific filter.
            itemsPerPage: 50,
        },
    };
}

export function singlePageFilterInput(filter: GridInput = {}) {
    return { input: singlePageFilter(filter) };
}

export function singleItem(filter: GridInput = {}) {
    return {
        ...filter,
        paging: {
            page: 1,
            itemsPerPage: 1,
        },
    };
}

export function singleItemInput(filter: GridInput = {}) {
    return { input: singleItem(filter) };
}

export function singleFilter(
    column: string,
    value: any,
    operator = 'EQ',
): GridInput {
    return {
        filter: [
            [
                {
                    column,
                    operator: 'EQ',
                    value: [value],
                },
            ],
        ],
    };
}

export function singleFilterInput(
    column: string,
    value: unknown,
    operator = 'EQ',
): { input: GridInput } {
    return { input: singleFilter(column, value, operator) };
}

export class FilterBuilder {
    private _filter: GridInput = {
        filter: [],
        sorter: [],
        groupBy: [],
    };

    constructor(simpleEqs: Record<KeyHints, string | number | any> = {}) {
        this.fillEqFilters(simpleEqs);
    }

    public whereAdditional(column: string, value: any, operator: string = 'EQ', filterFalseValues: boolean = true) {
        return this.where(column, value, operator, filterFalseValues, true);
    }

    public where(column: string, value: any, operator: string = 'EQ', filterFalseValues: boolean = true, asAdditionalFilter: boolean = false) {
        let values;
        if (!Array.isArray(value)) {
            values = [String(value)];
        } else {
            if (value.length === 0) return this;
            values = value.map(it => String(it));
            // Tohle tu bylo, aby se neposílali requesty na "0", ale pak je problém např. s EMPTY operátorem
            // Raději vyřešit tam, kde se posílá a neměl by "0"
            // if (filterFalseValues) {
            //     values = value.filter((it) => it).map((it) => it.toString());
            // }
        }

        let op = operator.toUpperCase();
        let field = column;
        ['IN', 'LIKE', 'EMPTY', 'NEMPTY'].find((it) => {
            const ends = `_${it}`;
            if (field.endsWith(ends)) {
                field = field.slice(0, -ends.length);
                op = it;
                return true;
            }
            return false;
        });

        if (op === 'EQ' && values.length > 1) {
            op = 'IN';
        } else if (op === 'NEQ' && values.length > 1) {
            op = 'NIN';
        }

        if (asAdditionalFilter) {
            this._filter.additionalFilter ??= [];
            this._filter.additionalFilter.push([
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            ]);
        } else {
            this._filter.filter ??= [];
            this._filter.filter!.push([
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            ]);
        }

        return this;
    }

    public orWhere(column: string, value: any, operator: string = 'EQ', filterFalseValues: boolean = true) {
        let values;
        if (!Array.isArray(value)) {
            values = [String(value)];
        } else {
            // Tohle tu bylo, aby se neposílali requesty na "0", ale pak je problém např. s EMPTY operátorem
            // Raději vyřešit tam, kde se posílá a neměl by "0"
            values = value;
            // if (filterFalseValues) {
            //     values = value.filter((it) => it).map((it) => it.toString());
            // }
        }

        let op = operator.toUpperCase();
        let field = column;
        ['IN', 'LIKE', 'EMPTY', 'NEMPTY'].find((it) => {
            const ends = `_${it}`;
            if (field.endsWith(ends)) {
                field = field.slice(0, -ends.length);
                op = it;
                return true;
            }
            return false;
        });

        if (this._filter.filter?.length) {
            this._filter.filter[this._filter.filter?.length - 1].push(
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            );
        } else {
            this._filter.filter!.push([
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            ]);
        }

        return this;
    }

    public active(active: boolean = true) {
        this.where('active', active);

        return this;
    }

    personId(personId: string | number) {
        this.where('personId', personId.toString());

        return this;
    }

    public folderId(folderId: string | number): this {
        this.where('folderId', folderId.toString());

        return this;
    }

    public ignoreFields(ignore: boolean = true): this {
        this._filter.ignoreFields = ignore;
        return this;
    }

    search(search: string[] | number[] | string | number | null | undefined, splitBySpaces: boolean = false, _detectDate: boolean = false) {
        if (search && search != '') {
            if (splitBySpaces && typeof search === 'string') {
                search = search.trim().split(/\s+/);
            }
            if (!Array.isArray(search)) {
                search = [(search as string | number).toString()];
            }
            // if (detectDate) { There are fuck ups with importing moment from date utils at build
            //     search.forEach((s) => {
            //         // If date is detected - use filter instead of search
            //         s = s.toString();
            //         if (s && isDate(s)) {
            //             this.setDateFilter(momentDate(s).toISOString());
            //             (search as string[]).splice((search as string[]).indexOf(s), 1);
            //         }
            //     });
            // }
            this._filter.search = search.map(s => s.toString());
        }

        return this;
    }

    serviceId(serviceId: string | number | null | undefined) {
        if (!serviceId) return this;
        this.where('serviceId', serviceId.toString());

        return this;
    }

    serviceIds(serviceIds: string[] | number[] | null | undefined) {
        if (!serviceIds) return this;
        this.where('serviceId', serviceIds.map(x => x.toString()), 'IN');

        return this;
    }

    public date(date: Date | string | null | undefined, key: string, operator?: string, allowEmpty: boolean = true) {
        if (date) {
            let op = operator || 'GTE';
            if (!operator && key.includes('dateTo')) {
                op = 'LTE';
            }

            this.where(key, new Date(date).toISOString(), op);
            if (allowEmpty) {
                this.orWhere(key, '', 'EMPTY');
            }
        }

        return this;
    }

    public dateRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        fromKey: string = 'dateFrom',
        toKey: string = 'dateTo',
    ) {
        this.date(dateFrom, fromKey);
        this.date(dateTo, toKey);
        return this;
    }

    public validAtRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        fromKey: string = 'validFrom',
        toKey: string = 'validTo',
    ) {
        this.date(dateFrom, fromKey, 'LTE');
        this.date(dateTo, toKey, 'GTE');
        return this;
    }

    public validInRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        dateKey: string = 'date',
    ) {
        this.date(dateFrom, dateKey, 'GTE');
        this.date(dateTo, dateKey, 'LTE');
        return this;
    }

    public overlapAtRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        fromKey: string = 'validFrom',
        toKey: string = 'validTo',
        allowEmpty: boolean = true,
    ) {
        if (dateFrom) {
            this.where(toKey, new Date(dateFrom).toISOString(), 'GTE');
            allowEmpty && this.orWhere(toKey, 'true', 'EMPTY');
        }
        if (dateTo) {
            this.where(fromKey, new Date(dateTo).toISOString(), 'LTE');
            allowEmpty && this.orWhere(fromKey, 'true', 'EMPTY');
        }

        return this;
    }

    public validAt(date: Date | string | null | undefined, fromKey: string = 'validFrom', toKey: string = 'validTo') {
        const now = new Date(date || new Date());
        this.validAtRange(now, now, fromKey, toKey);
        return this;
    }

    public validByNow(fromKey: string = 'validFrom', toKey: string = 'validTo') {
        this.validAt(new Date(), fromKey, toKey);
        return this;
    }

    organizationId(organizationId: string | number) {
        this.where('organizationId', organizationId.toString());

        return this;
    }

    public type(type: string) {
        this.where('type', type);

        return this;
    }

    public withDeleted() {
        this.where('deletedAt', '1', 'EMPTY');

        return this;
    }

    public workType(workType: string) {
        this.where('workType', workType);

        return this;
    }

    public sort(column: string, direction: 'ASC' | 'DESC' = 'ASC'): FilterBuilder {
        this._filter.sorter ??= [];
        this._filter.sorter?.push({
            column,
            direction,
        });

        return this;
    }

    public group(column: string): FilterBuilder {
        this._filter.groupBy ??= [];
        this._filter.groupBy?.push({ column });

        return this;
    }

    limit(limit: number) {
        this._filter.paging = {
            page: this._filter?.paging?.page ?? 1,
            itemsPerPage: limit,
        };

        return this;
    }

    page(page: number) {
        this._filter.paging = {
            page,
            itemsPerPage: this._filter?.paging?.itemsPerPage ?? 10,
        };

        return this;
    }

    withFilter(filter: FilterInput[][] | null) {
        if (filter) {
            this._filter.filter = filter;
        }
        return this;
    }

    create() {
        return this._filter;
    }

    input() {
        return {
            input: this._filter,
        };
    }

    filter() {
        return this._filter;
    }

    singlePage() {
        return singlePageFilterInput(this._filter).input;
    }

    singlePageInput() {
        return singlePageFilterInput(this._filter);
    }

    public static fromGridInput(input: GridInput | IGridRequestDto | null = null): FilterBuilder {
        const fb = new FilterBuilder();
        if (input) {
            fb._filter = input as any;
            fb._filter.filter ??= [];
            fb._filter.sorter ??= [];
            fb._filter.groupBy ??= [];
        }

        return fb;
    }

    public static singlePageInput(simpleEqs: Record<KeyHints, string | number | any> = {}) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder.singlePageInput();
    }

    static singlePage(simpleEqs: Record<KeyHints, string | number | any> = {}) {
        return FilterBuilder.singlePageInput(simpleEqs).input;
    }

    static input(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder.input();
    }

    static filter(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder._filter;
    }

    public static filterStr(simpleEqs: Record<KeyHints, string | number | any>): string {
        const builder = new FilterBuilder(simpleEqs);
        return builder.filterStr();
    }

    public filterStr(): string {
        const str = JSON.stringify(this._filter);

        return str
            .replaceAll('"filter":', 'filter:')
            .replaceAll('"column":', 'column:')
            .replaceAll('"value":', 'value:')
            .replaceAll('"operator":', 'operator:')
            .replaceAll('"sorter":', 'sorter:')
            .replaceAll('"direction":', 'direction:')
            .replaceAll('"paging":', 'paging:')
            .replaceAll('"page":', 'page:')
            .replaceAll('"itemsPerPage":', 'itemsPerPage:')
            .replaceAll('"search":', 'search:')
            .replaceAll('"groupBy":', 'groupBy:')
            ;
    }

    public static where(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder;
    }

    public static filterById(id: string | number, key: string = 'id') {
        const builder = new FilterBuilder();
        builder.where(key, id);

        return builder._filter;
    }

    public static additionalFilter(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder._filter.filter;
    }

    static singleItemInput(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);
        builder.limit(1);

        return builder.input();
    }

    static singleItem(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);
        builder.limit(1);

        return builder._filter;
    }

    private fillEqFilters(simpleEqs: Record<KeyHints, string | number | any>) {
        Object.keys(simpleEqs).forEach((key) => {
            switch (key) {
                case 'limit':
                    this.limit(Number(simpleEqs[key]));
                    break;
                case 'page':
                    this.page(Number(simpleEqs[key]));
                    break;
                case 'search':
                    this.search(simpleEqs[key]);
                    break;
                case 'withDeleted':
                    this._filter.withDeleted = Boolean(simpleEqs[key]);
                    break;
                case 'sort': {
                    const sorter = simpleEqs[key];
                    if (Array.isArray(sorter)) {
                        sorter.forEach((sort) => {
                            this.sort(sort.column, sort.direction || 'ASC');
                        });
                    } else {
                        this.sort(sorter.column, sorter.direction || 'ASC');
                    }
                    break;
                }
                default:
                    if (Array.isArray(simpleEqs[key])) {
                        this.where(key, simpleEqs[key].map((it: any) => it.toString()), 'IN');
                    } else if (simpleEqs[key] && typeof simpleEqs[key] === 'object') {
                        const op = Object.keys(simpleEqs[key])[0];
                        let values: any = simpleEqs[key][op];
                        if (!Array.isArray(values)) {
                            values = [values];
                        }
                        this.where(key, values, op);
                    } else if (simpleEqs[key] !== undefined && simpleEqs[key] !== null) {
                        this.where(key, simpleEqs[key].toString());
                    }
            }
        });
    }

    private setDateFilter(date: Date | string) {
        // Set to the beginning of the day
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);

        // Set to the beginning of the next day
        const endOfDay = new Date(date);
        endOfDay.setDate(endOfDay.getDate() + 1);
        endOfDay.setHours(0, 0, 0, 0);

        this.where('date', [startOfDay.toISOString(), endOfDay.toISOString()], 'BETWEEN');
    }
}
